import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { Box, Skeleton } from '@machobear/design-system'

import CloseButton from '@/components/close-button'
import { Label } from '@/components/text'

import { CheckboxSection } from '../elements'
import {
  Content,
  ContentWrapper,
  FilterGroup,
  FilterItem,
  FilterLabel,
  Header,
  Wrapper
} from './elements'

const StyledCloseButton = styled(CloseButton)`
  position: unset;
`

const Loading = () => {
  const { t } = useTranslation()
  return (
    <Wrapper>
      <Header>
        <Label fontSize="18px" type="large">
          {t('filters:b.listing_filter.edit.filters.label', 'Filters')}
        </Label>
        <StyledCloseButton id="map-filters-cancel-click" onClick={close} />
      </Header>
      <Content>
        <ContentWrapper>
          <FilterGroup>
            <FilterItem span={2}>
              <FilterLabel>
                {t('filters:b.listing_filter.edit.price.label', 'Price')}
              </FilterLabel>
              <Skeleton.Shape width="100%" height="96px" />
            </FilterItem>
          </FilterGroup>
          <FilterGroup>
            <FilterItem span={2}>
              <FilterLabel size="small">
                {t(
                  'filters:b.listing_filter.edit.I_want_to_rent.label',
                  'I want to rent'
                )}
              </FilterLabel>
              <Skeleton.Shape width="100%" height="60px" />
            </FilterItem>
          </FilterGroup>
          <FilterGroup>
            <FilterItem span={2}>
              <FilterLabel size="small">
                {t(
                  'filters:b.listing_filter.edit.rental_type.label',
                  'Rental Type'
                )}
              </FilterLabel>
              <Skeleton.Shape width="100%" height="60px" />
            </FilterItem>
          </FilterGroup>
          <FilterGroup>
            <FilterItem span={2}>
              <FilterLabel size="small">
                {t('filters:b.listing_filter.edit.bedroom.label', 'Bedrooms')}
              </FilterLabel>
              <Skeleton.Shape width="100%" height="60px" />
            </FilterItem>
            <FilterItem span={2}>
              <FilterLabel size="small">
                {t('filters:b.listing_filter.edit.bathroom.label', 'Bathrooms')}
              </FilterLabel>
              <Skeleton.Shape width="100%" height="60px" />
            </FilterItem>
          </FilterGroup>
          <FilterGroup>
            <FilterItem span={2}>
              <FilterLabel size="small">
                {t(
                  'filters:b.listing_filter.edit.furnishing.label',
                  'Furnishing'
                )}
              </FilterLabel>
              <Skeleton.Shape width="100%" height="60px" />
            </FilterItem>
          </FilterGroup>
          <FilterGroup>
            <FilterItem span={2}>
              <FilterLabel>
                {t(
                  'filters:b.listing_filter.edit.features_amenities.label',
                  'Features & Amenities'
                )}
              </FilterLabel>
              <CheckboxSection>
                <Skeleton.Shape width="185px" height="28px" />
                <Skeleton.Shape width="185px" height="28px" />
                <Skeleton.Shape width="185px" height="28px" />
                <Skeleton.Shape width="185px" height="28px" />
                <Skeleton.Shape width="185px" height="28px" />
                <Skeleton.Shape width="185px" height="28px" />
                <Skeleton.Shape width="185px" height="28px" />
                <Skeleton.Shape width="185px" height="28px" />
              </CheckboxSection>
            </FilterItem>
          </FilterGroup>
          <FilterGroup>
            <FilterItem span={2}>
              <FilterLabel>
                {t('filters:b.listing_filter.edit.deals.label', 'Deals')}
              </FilterLabel>
              <CheckboxSection>
                <Skeleton.Shape width="185px" height="28px" />
                <Skeleton.Shape width="185px" height="28px" />
              </CheckboxSection>
            </FilterItem>
          </FilterGroup>
          <FilterGroup>
            <FilterItem span={2}>
              <FilterLabel>
                {t(
                  'filters:b.listing_filter.edit.utilities.label',
                  'Utilities'
                )}
              </FilterLabel>
              <CheckboxSection>
                <Skeleton.Shape width="185px" height="28px" />
                <Skeleton.Shape width="185px" height="28px" />
                <Skeleton.Shape width="185px" height="28px" />
                <Skeleton.Shape width="185px" height="28px" />
                <Skeleton.Shape width="185px" height="28px" />
                <Skeleton.Shape width="185px" height="28px" />
              </CheckboxSection>
            </FilterItem>
          </FilterGroup>
          <FilterGroup>
            <FilterItem span={2}>
              <FilterLabel>
                {t('filters:b.listing_filter.edit.pets.label', 'Pets')}
              </FilterLabel>
              <CheckboxSection>
                <Skeleton.Shape width="185px" height="28px" />
                <Skeleton.Shape width="185px" height="28px" />
                <Skeleton.Shape width="185px" height="28px" />
              </CheckboxSection>
            </FilterItem>
          </FilterGroup>
          <FilterGroup>
            <FilterItem>
              <FilterLabel>
                {t(
                  'filters:b.listing_filter.edit.availability.label',
                  'Availability'
                )}
              </FilterLabel>
              <Skeleton.Shape width="100%" height="60px" />
            </FilterItem>
            <FilterItem>
              <Box
                display="flex"
                alignItems="center"
                height="full"
                paddingTop={{ mobile: 'none', tablet: 'medium' }}
              >
                <Skeleton.Shape width="100%" height="28px" />
              </Box>
            </FilterItem>
          </FilterGroup>
          <FilterGroup>
            <FilterItem>
              <FilterLabel>
                {t(
                  'filters:b.listing_filter.edit.size_sqft.label',
                  'Size (sqft)'
                )}
              </FilterLabel>
              <Skeleton.Shape width="100%" height="60px" />
            </FilterItem>
            <FilterItem>
              <FilterLabel>
                {t('filters:b.listing.view.lease_type.text', 'Lease Type')}
              </FilterLabel>
              <Skeleton.Shape width="100%" height="60px" />
            </FilterItem>
          </FilterGroup>
          <FilterGroup>
            <FilterItem>
              <FilterLabel>
                {t('filters:b.listing_filter.edit.sort_by.label', 'Sort By')}
              </FilterLabel>
              <Skeleton.Shape width="100%" height="60px" />
            </FilterItem>
          </FilterGroup>
          <FilterGroup noBorder>
            <FilterItem span={2}>
              <FilterLabel marginBottom="small">
                {t('common.other', 'Other')}
              </FilterLabel>
              <Skeleton.Shape width="160px" height="28px" />
              <Skeleton.Shape width="250px" height="28px" />
            </FilterItem>
          </FilterGroup>
        </ContentWrapper>
      </Content>
      <Box
        display="flex"
        justifyContent="flexEnd"
        alignItems="center"
        position={{ mobile: 'fixed', tablet: 'sticky' }}
        background="neutralLight"
        borderTop="borderNeutralLight"
        width="full"
        left={0}
        right={0}
        bottom={0}
        paddingX="gutter"
        paddingY="small"
        style={{
          zIndex: 100
        }}
      >
        <Skeleton.Shape width="180px" height="40px" />
      </Box>
    </Wrapper>
  )
}

export default Loading
